import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Button, Stack } from '@mui/material';
import { LegalPerson, IdentityCheckFlowLegalPerson, IdentifiedDevelopment } from '~/api/types';
import { DevelopmentSelectionButton } from '~/components/button/development-selection-button';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';

/**
 * The developments are already narrowed by the backend to the ones served at
 * the current host.
 */
export function DevelopmentSelectionScreen<T extends LegalPerson | IdentityCheckFlowLegalPerson>({
  person,
  onDevelopmentSelection,
  goBack,
  title,
}: DevelopmentSelectionScreenProps<T>) {
  const allDevelopments: T['developments'][number][] = person.developments;

  // the backend only redacts the development of a legal person that has no other
  const redactedDevelopment = allDevelopments.find((d) => d.isRedacted);

  const developments = allDevelopments
    .filter((d): d is T['developments'][number] & IdentifiedDevelopment => !d.isRedacted)
    .sort((a, b) => a.companyName.localeCompare(b.companyName));

  const hasMultipleDevelopments = developments.length > 1;

  const message = hasMultipleDevelopments
    ? 'Encontramos mais de um contrato no seu nome. Selecione uma opção para continuar:'
    : 'Selecione uma opção para continuar:';

  if (redactedDevelopment) {
    const { customerPortalUrl } = redactedDevelopment;

    // nothing about the development is shown, so the customer is only offered
    // to go on: to the customer portal when already verified for it
    const onClick = () => {
      if (customerPortalUrl === null) {
        onDevelopmentSelection(redactedDevelopment);
      } else {
        window.open(customerPortalUrl, '_blank')?.focus();
      }
    };

    return (
      <Page>
        <Title>{title}</Title>
        <Stack gap={2} mt={3} alignItems={'center'}>
          <Button onClick={onClick} variant="contained" fullWidth sx={{ fontWeight: 'bold' }}>
            {customerPortalUrl === null ? 'Continuar' : 'Acessar área do cliente'}
          </Button>
          <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
            Voltar
          </Button>
        </Stack>
      </Page>
    );
  }

  if (developments.length === 0)
    return (
      <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
        <Title>Nenhum empreendimento encontrado</Title>
        <Box width={'444px'}>
          <Body1>Verifique os dados informados ou entre em contato com o atendimento</Body1>
        </Box>
        <Stack gap={2} mt={3} alignItems={'center'}>
          <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
            Voltar
          </Button>
        </Stack>
      </Page>
    );

  return (
    <Page>
      <Title>{title}</Title>
      <Box width={{ xs: '100%' }}>
        <Body1>{message}</Body1>
      </Box>
      <Box>
        {developments.map((development, idx) => (
          <DevelopmentSelectionButton
            key={idx}
            development={development}
            onClickWhenUrlIsNull={() => onDevelopmentSelection(development)}
          />
        ))}
      </Box>
      <Stack gap={2} mt={3} alignItems={'center'}>
        <Button startIcon={<ChevronLeftIcon />} onClick={goBack} sx={{ fontWeight: 'bold' }}>
          Voltar
        </Button>
      </Stack>
    </Page>
  );
}

interface DevelopmentSelectionScreenProps<T extends LegalPerson | IdentityCheckFlowLegalPerson> {
  person: T;
  onDevelopmentSelection: (customer: T['developments'][number]) => void;
  goBack: () => void;
  title: string;
}
